/**
 * `PinnedColumnShadows` — the continuous drop shadow cast by pinned columns onto the
 * scrolling content beneath them. ONE full-height gradient rail per side, rendered once as
 * an overlay OUTSIDE the scroll flow, replacing the old per-cell `box-shadow` that restarted
 * at every floating row (the 1px inter-row gaps + `rounded-sm` corners chopped it into
 * per-row segments).
 *
 * PLACEMENT: mount inside the NON-scrolling `position: relative` wrapper that bounds the
 * scroll container (the preset's card). Each rail is `absolute inset-y-0` at the pinned
 * group's inner edge (`left`/`right` = the group's sticky gutter width from
 * `getPinnedGutterWidths`). Because it lives on the wrapper — not inside the scroll
 * container — it does not scroll vertically (so the one element spans the full VISIBLE
 * height → continuous under vertical scroll) nor horizontally (so it stays glued to the pin
 * edge, exactly like the sticky columns it shadows). `pointer-events-none` + the low `z-[1]`
 * keep it a pure visual overlay: above the scrolled cells, out of interaction. The rail sits
 * to the OUTER side of its gutter (over unpinned content), never over the pinned cells.
 *
 * The RIGHT rail additionally adds `scrollbarWidth`: the wrapper edge sits OUTSIDE the scroll
 * container's vertical scrollbar, but right-pinned columns stick to the scrollport INSIDE it,
 * so without this the right rail would drift over the pinned cells by the scrollbar width
 * (visible on classic/Firefox scrollbars; ~6px on WebKit; 0 with overlay scrollbars).
 *
 * VISIBILITY: each rail shows only when content is actually hidden under its group
 * (`showLeft` / `showRight`, from `useHorizontalOverflowShadows`) — a pinned column with
 * nothing scrolled beneath it casts no shadow (MUI pinned-shadow parity). The toggle is an
 * opacity fade so the shadow eases in on the first scroll instead of popping. A side with no
 * pins (gutter `0`) renders no rail at all.
 *
 * KNOWN, ACCEPTED cosmetic (the shadow doing its job, not a bug): at the pin boundary the
 * rail paints over the first px of adjacent unpinned content — including a keyboard-active
 * cell's ring edge — because it sits above `TableRoot`'s isolate context; this is the same
 * intended boundary-darkening MUI applies.
 */
import { cn } from '@gto/ui/cn'

import { PIN_SHADOW_WIDTH_PX, pinnedShadowRailClass } from './pinning.styles'

export type PinnedColumnShadowsProps = {
  /** Sticky px width of the left pinned group (`getPinnedGutterWidths().left`); `0` → no left rail. */
  left: number
  /** Sticky px width of the right pinned group; `0` → no right rail. */
  right: number
  /** Show the left rail — content is scrolled under the left-pinned group. */
  showLeft: boolean
  /** Show the right rail — content is scrolled under the right-pinned group. */
  showRight: boolean
  /**
   * Vertical-scrollbar width (px) from `useHorizontalOverflowShadows`. The right-pinned
   * columns stick to the scrollport (INSIDE the scrollbar), but this overlay is anchored to
   * the outer card (OUTSIDE it), so the right rail adds this back to stay glued to the pin
   * edge instead of drifting over the pinned cells. Only the right rail needs it (LTR has no
   * left scrollbar); defaults to `0`.
   */
  scrollbarWidth?: number
}

const RAIL_BASE = 'pointer-events-none absolute inset-y-0 z-[1] transition-opacity duration-200'

/** Continuous pinned-column shadow rails. See the file header for placement + visibility. */
export const PinnedColumnShadows = ({
  left,
  right,
  showLeft,
  showRight,
  scrollbarWidth = 0,
}: PinnedColumnShadowsProps) => (
  <>
    {left > 0 && (
      <div
        aria-hidden
        data-pin-shadow="left"
        style={{ left, width: PIN_SHADOW_WIDTH_PX }}
        className={cn(
          RAIL_BASE,
          pinnedShadowRailClass.left,
          showLeft ? 'opacity-100' : 'opacity-0',
        )}
      />
    )}
    {right > 0 && (
      <div
        aria-hidden
        data-pin-shadow="right"
        style={{ right: right + scrollbarWidth, width: PIN_SHADOW_WIDTH_PX }}
        className={cn(
          RAIL_BASE,
          pinnedShadowRailClass.right,
          showRight ? 'opacity-100' : 'opacity-0',
        )}
      />
    )}
  </>
)
